import React, { useMemo } from 'react';

import { StepProps, track } from '@betterment/js-runtime';
import { Box, Button, FormikField, H2, Input, Select, Text, VStack } from '@betterment/samba-react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { CustomerSignupValues } from './Controller';

import { CheckingSignupData, CheckingSignupDataParams } from '../Shared/data';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';

const FORMER_EMPLOYMENT_STATUSES: Set<string> = new Set(['unemployed', 'retired']);

const EXCLUDED_OCCUPATIONS = new Set(['N/A', 'None', 'Other']);

const STUDENT_STATUSES = new Set(['student', 'foreign_student']);

type EmploymentCategory = 'student' | 'former' | 'active';

const getEmploymentCategory = (status: string): EmploymentCategory => {
  if (STUDENT_STATUSES.has(status)) return 'student';
  if (FORMER_EMPLOYMENT_STATUSES.has(status)) return 'former';
  return 'active';
};

const LEGACY_TO_V2_EMPLOYMENT_STATUS: Record<string, string> = {
  full_time: 'employed',
  part_time: 'employed',
  homemaker: 'unemployed',
  disabled: 'unemployed',
};

const mapEmploymentStatusToV2 = (status?: string): string => {
  if (status == null) return '';
  return LEGACY_TO_V2_EMPLOYMENT_STATUS[status] ?? status;
};

const findMatchingRole = (role: string | undefined | null, roleOptions: string[]): string => {
  if (!role) return '';
  const lowerRole = role.toLowerCase();
  return roleOptions.find(opt => opt.toLowerCase() === lowerRole) ?? '';
};

interface InitialFormValues {
  employmentStatus: string;
  occupation: string;
  employerName: string;
}

interface GetInitialFormValuesParams {
  flowEmploymentStatus?: string;
  flowRole?: string;
  flowEmployerName?: string;
  userProfession: CheckingSignupData['userProfession'];
  roleOptions: string[];
  employmentStatusKeys: Set<string>;
}

const getInitialFormValues = ({
  flowEmploymentStatus,
  flowRole,
  flowEmployerName,
  userProfession,
  roleOptions,
  employmentStatusKeys,
}: GetInitialFormValuesParams): InitialFormValues => {
  const rawEmploymentStatus = flowEmploymentStatus ?? userProfession?.employmentStatus ?? '';
  const mappedStatus = mapEmploymentStatusToV2(rawEmploymentStatus);
  const rawRole = flowRole ?? userProfession?.role ?? '';
  return {
    employmentStatus: employmentStatusKeys.has(mappedStatus) ? mappedStatus : '',
    occupation: findMatchingRole(rawRole, roleOptions),
    employerName: flowEmployerName ?? userProfession?.employerName ?? '',
  };
};

interface OccupationSelectFieldProps {
  name: string;
  label: string;
  options: string[];
}

const NewFlowStepSchema = Yup.object().shape({
  employmentStatus: Yup.string().required('Employment status is required'),
  role: Yup.string().when('employmentStatus', (values, schema) =>
    getEmploymentCategory(String(values[0])) === 'student' ? schema : schema.required('Occupation is required')
  ),
  employerName: Yup.string().when('employmentStatus', (values, schema) =>
    getEmploymentCategory(String(values[0])) === 'student' ? schema : schema.required('Employer is required')
  ),
});

export const OccupationSelectField: React.FC<OccupationSelectFieldProps> = ({ name, label, options }) => (
  <FormikField name={name}>
    <FormikField.Label>{label}</FormikField.Label>
    <FormikField.Control>
      <Select>
        <Select.Trigger aria-label={`Select ${label.toLowerCase()}`}>
          <Select.Value placeholder="Select..." />
        </Select.Trigger>
        <Select.Content>
          {options.map(value => (
            <Select.Item key={value} value={value}>
              {value}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    </FormikField.Control>
    <FormikField.ErrorMessage />
  </FormikField>
);

interface EmployerFieldProps {
  name: string;
  label: string;
}

const EmployerField: React.FC<EmployerFieldProps> = ({ name, label }) => (
  <FormikField name={name}>
    <FormikField.Label>{label}</FormikField.Label>
    <FormikField.Control>
      <Input type="text" />
    </FormikField.Control>
    <FormikField.ErrorMessage />
  </FormikField>
);

export interface EmploymentFormProps extends StepProps<CustomerSignupValues> {
  checkingSignupData: CheckingSignupData;
  persistCheckingSignupData: (values: CheckingSignupDataParams) => Promise<void>;
  handleClose: () => void;
}

interface InnerFormProps extends Pick<StepProps<CustomerSignupValues>, 'goToPreviousStep' | 'goToNextStep'> {
  stepTitle: string;
  initialEmploymentStatus: string;
  initialOccupation: string;
  initialEmployerName: string;
  filteredRoleOptions: string[];
  employmentStatusOptions: Record<string, string>;
  sourceOfFunds?: string;
  persistCheckingSignupData: (values: CheckingSignupDataParams) => Promise<void>;
  handleClose: () => void;
}

const InnerForm: React.FC<InnerFormProps> = ({
  stepTitle,
  initialEmploymentStatus,
  initialOccupation,
  initialEmployerName,
  filteredRoleOptions,
  employmentStatusOptions,
  sourceOfFunds,
  goToPreviousStep,
  goToNextStep,
  handleClose,
  persistCheckingSignupData,
}) => {
  const handleSubmit = async (values: { employmentStatus: string; role: string; employerName: string }) => {
    track('ElementClicked', { name: 'Continue', flow: 'OpenCheckingAccount' });
    const isStudent = getEmploymentCategory(values.employmentStatus) === 'student';

    await persistCheckingSignupData({
      fundingSourceOption: sourceOfFunds ?? '',
      roleOption: isStudent ? undefined : values.role,
      employmentStatus: values.employmentStatus,
      employerName: isStudent ? undefined : values.employerName || undefined,
    });

    goToNextStep({
      role: isStudent ? '' : values.role,
      employmentStatus: values.employmentStatus,
      employerName: isStudent ? '' : values.employerName,
    });
  };

  return (
    <WorkflowLayout
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <H2 color="text">Confirm your employment.</H2>
      <Box css={{ marginY: 'space.200' }}>
        <Text as="p" color="text" css={{ marginBottom: 'space.100' }}>
          We&apos;re required by our regulators to collect this information.
        </Text>
      </Box>
      <Formik
        onSubmit={handleSubmit}
        validationSchema={NewFlowStepSchema}
        initialValues={{
          employmentStatus: initialEmploymentStatus,
          role: initialOccupation,
          employerName: initialEmployerName,
        }}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting, values: formValues }) => {
          const category = getEmploymentCategory(formValues.employmentStatus);
          const occupationLabel = category === 'former' ? 'Former occupation' : 'Occupation';
          const employerLabel = category === 'former' ? 'Former employer' : 'Employer';

          return (
            <Form>
              <VStack css={{ gap: 'space.300', width: '100%' }}>
                <FormikField name="employmentStatus">
                  <FormikField.Label>Employment status</FormikField.Label>
                  <FormikField.Control>
                    <Select>
                      <Select.Trigger aria-label="Select employment status">
                        <Select.Value placeholder="Select..." />
                      </Select.Trigger>
                      <Select.Content>
                        {Object.entries(employmentStatusOptions).map(([key, displayName]) => (
                          <Select.Item key={key} value={key}>
                            {displayName}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select>
                  </FormikField.Control>
                  <FormikField.ErrorMessage />
                </FormikField>

                {category !== 'student' && (
                  <>
                    <Box>
                      <OccupationSelectField name="role" label={occupationLabel} options={filteredRoleOptions} />
                      {formValues.employmentStatus === 'unemployed' && (
                        <Text as="p" color="text.weak" css={{ mt: 'space.100' }}>
                          Please choose the option that best describes your situation.
                        </Text>
                      )}
                    </Box>
                    <EmployerField name="employerName" label={employerLabel} />
                  </>
                )}
              </VStack>

              <Button
                type="submit"
                css={{ mt: 'space.400', width: { base: '100%', md: 'auto' } }}
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </Form>
          );
        }}
      </Formik>
    </WorkflowLayout>
  );
};

const EmploymentForm: React.FC<EmploymentFormProps> = ({
  flowState,
  goToNextStep,
  goToPreviousStep,
  checkingSignupData,
  handleClose,
  persistCheckingSignupData,
}) => {
  const {
    stepTitle,
    role: flowRole,
    employmentStatus: flowEmploymentStatus,
    employerName: flowEmployerName,
    sourceOfFunds,
  } = flowState.values;

  const filteredRoleOptions = useMemo(
    () => checkingSignupData.roleOptions.filter(opt => !EXCLUDED_OCCUPATIONS.has(opt)),
    [checkingSignupData.roleOptions]
  );

  const employmentStatusOptions = useMemo(
    () => Object.fromEntries((checkingSignupData.employmentStatusOptions ?? []).map(opt => [opt.key, opt.displayName])),
    [checkingSignupData.employmentStatusOptions]
  );

  const employmentStatusKeys = useMemo(() => new Set(Object.keys(employmentStatusOptions)), [employmentStatusOptions]);

  const {
    employmentStatus: initialEmploymentStatus,
    occupation: initialOccupation,
    employerName: initialEmployerName,
  } = getInitialFormValues({
    flowEmploymentStatus,
    flowRole,
    flowEmployerName,
    userProfession: checkingSignupData.userProfession,
    roleOptions: checkingSignupData.roleOptions,
    employmentStatusKeys,
  });

  return (
    <InnerForm
      stepTitle={stepTitle}
      initialEmploymentStatus={initialEmploymentStatus}
      initialOccupation={initialOccupation}
      initialEmployerName={initialEmployerName}
      filteredRoleOptions={filteredRoleOptions}
      employmentStatusOptions={employmentStatusOptions}
      sourceOfFunds={sourceOfFunds}
      goToPreviousStep={goToPreviousStep}
      goToNextStep={goToNextStep}
      handleClose={handleClose}
      persistCheckingSignupData={persistCheckingSignupData}
    />
  );
};

export default EmploymentForm;
